/**
 * HOME. UF_02.
 *
 * "Home never moves value: it shows and it routes."
 *
 * That rule is why this page has no Send form on it. A screen that both summarises and transacts is
 * being careful about two things at once, and the one it eventually gets wrong is the one that moves
 * money. Home shows the state of things and points at the place where each thing is done.
 *
 * What it shows, in the order a person actually wants it:
 *
 *   1. What have I got            (balances, and what is locked OUT of them)
 *   2. What can I do right now    (quick actions, gated by tier so no button lies)
 *   3. What is in my way          (verification, a hold, a cap running out)
 *   4. What just happened         (recent activity)
 *
 * The visual centrepiece is the balance card: a pine-green surface that carries the brand and gives
 * the one number a person opens the app to see the weight it deserves. Everything else is quieter.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type Limits, type Lock, type Wallet } from '@/lib/api';
import { CapHeadroom } from '@/components/patterns';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';
import { atLeast, useSession } from '@/lib/session-context';

/** A reset timestamp in the words a person uses, per the cap pattern (UF-00 6.3). */
function resetPhrase(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? `midnight tonight`
    : d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

type History = Awaited<ReturnType<typeof api.history>>['transactions'];

export default function Home() {
  const { me, tier } = useSession();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [locks, setLocks] = useState<Lock[]>([]);
  const [history, setHistory] = useState<History>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;

    setError(null);
    try {
      const [w, l, h, lim] = await Promise.all([
        api.wallet(me.participant_id),
        api.locks(me.participant_id),
        api.history(me.participant_id),
        // Cap headroom, shown BEFORE the participant tries to act (UF-02 5.4, the cap pattern).
        api.limits(me.participant_id).catch(() => null),
      ]);
      setWallet(w);
      setLocks(l.locks);
      setHistory(h.transactions.slice(0, 5));
      setLimits(lim);
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not reach your wallet just now.',
      );
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    void load();
  }, [load]);

  // A T0 participant HAS no wallet: the backend opens one at T1. Asking for it and then showing an
  // error would be blaming the user for a state the platform put them in.
  if (tier === 'T0') return <NotVerifiedYet />;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-40 animate-shimmer rounded-xl" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-24 animate-shimmer rounded-card" />
          ))}
        </div>
        <LoadingCard lines={2} />
      </div>
    );
  }

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;

  const kes = wallet?.balances.find((b) => b.asset === 'KES');

  return (
    // The dashboard grid. One column on mobile (the phone experience is unchanged); at lg it splits
    // into a wide main column and a narrower activity rail, so a desktop screen carries far more at a
    // glance instead of a single phone-width strip. `items-start` keeps the rail from stretching to
    // the main column's height.
    <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      <div className="space-y-6 lg:col-span-2">
        {/* 1. WHAT HAVE I GOT. The hero balance card. */}
        <Reveal>
          <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
            {/* Soft brand glow, purely decorative, kept out of the accessibility tree. */}
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-16 -left-8 h-44 w-44 rounded-full bg-accent/20 blur-3xl"
            />

            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="text-sm text-white/70">{greeting()}</p>
                <span className="rounded-pill bg-white/15 px-2.5 py-0.5 text-micro font-medium tracking-wide">
                  VASP
                </span>
              </div>

              <p className="mt-4 text-sm text-white/70">Available to spend</p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-sm font-medium text-white/70">KES</span>
                <span className="tabular text-4xl font-semibold tracking-tight">
                  {formatWireAmount(kes?.available) || '0.00'}
                </span>
              </p>

              {/*
              LOCKED MONEY IS SHOWN, ALWAYS, beside the available figure rather than buried.

              "Insufficient balance" while the balance on screen looks perfectly adequate is the most
              maddening thing a wallet can say, and the reason is that some of the money is held in
              escrow. Telling the participant where it went, BEFORE they try to spend it, is most of
              what "compliance is visible, not hidden" means in practice.
            */}
              {kes && kes.locked !== '0.00' ? (
                <Link
                  to="/wallet"
                  className="mt-4 flex items-center justify-between rounded-lg bg-white/10 px-3 py-2 text-sm backdrop-blur transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                  <span className="text-white/80">Held in escrow, not available to spend</span>
                  <span className="tabular font-medium">KES {formatWireAmount(kes.locked)}</span>
                </Link>
              ) : null}
            </div>
          </section>
        </Reveal>

        {wallet?.frozen ? (
          <p className="rounded-card border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
            Your wallet is frozen while we complete a check. Your money is safe and nothing has been
            taken from it. We will be in touch.
          </p>
        ) : null}

        {/*
          2. WHAT CAN I DO. Gated, so a button never promises something the backend will refuse.

          The four money actions come first and in the order money moves through the app: out
          (Send), between assets (Convert), in (Crypto Till), and out of the platform entirely
          (Withdraw). Receive and Tokenize follow, because one is the same job as a Till by a
          different route and the other is a different product.

          Two per row on a phone, three at sm, six on a desktop: the tap targets stay the size they
          were, and nothing scrolls sideways.
        */}
        <Stagger className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StaggerItem>
            <QuickAction
              href="/transact"
              label="Send money"
              enabled={atLeast(tier, 'T2')}
              locked="Tier 2"
              icon={<SendIcon />}
            />
          </StaggerItem>
          <StaggerItem>
            <QuickAction
              href="/transact"
              label="Convert"
              enabled={atLeast(tier, 'T2')}
              locked="Tier 2"
              icon={<ConvertIcon />}
            />
          </StaggerItem>
          <StaggerItem>
            {/* Receiving, not moving: a till needs an identity behind it, not a sending tier. */}
            <QuickAction
              href="/till"
              label="Crypto Till"
              enabled={atLeast(tier, 'T1')}
              locked="Tier 1"
              icon={<TillIcon />}
            />
          </StaggerItem>
          <StaggerItem>
            <QuickAction
              href="/wallet/withdraw"
              label="Withdraw"
              enabled={atLeast(tier, 'T2')}
              locked="Tier 2"
              icon={<WithdrawIcon />}
            />
          </StaggerItem>
          <StaggerItem>
            <QuickAction
              href="/receive"
              label="Receive"
              enabled={atLeast(tier, 'T1')}
              locked="Tier 1"
              icon={<ReceiveIcon />}
            />
          </StaggerItem>
          <StaggerItem>
            <QuickAction
              href="/tokenize"
              label="Tokenize"
              enabled={atLeast(tier, 'T2')}
              locked="Tier 2"
              icon={<TokenizeIcon />}
            />
          </StaggerItem>
        </Stagger>

        {/* CAP HEADROOM. Shown before composing, so a person sees what they have left rather than
          discovering a cap at the moment it refuses them (UF-02 5.4, FR-X-GOV-001 to 004). */}
        {limits ? (
          limits.available ? (
            <Reveal className="space-y-3">
              <CapHeadroom
                label="Daily limit"
                used={limits.daily.used}
                limit={limits.daily.limit}
                asset={limits.asset}
                resets={resetPhrase(limits.daily.resets_at)}
              />
              <CapHeadroom
                label="Total sandbox limit"
                used={limits.cumulative.used}
                limit={limits.cumulative.limit}
                asset={limits.asset}
              />
            </Reveal>
          ) : (
            <section className="rounded-card border border-warning/30 bg-warning/5 p-4 text-sm text-ink-muted">
              Your transaction limits are being configured. Until they are set, movements are held
              rather than allowed, because the sandbox caps fail closed.
            </section>
          )
        ) : null}

        {/* 3. WHAT IS IN MY WAY. */}
        {tier === 'T1' ? (
          <Link
            to="/verify/tier2"
            className="flex items-center gap-3 rounded-card border border-warning/30 bg-warning/5 p-4 transition hover:bg-warning/10"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
              <LockIcon />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-ink">
                You can hold money, but not send it yet
              </span>
              <span className="mt-0.5 block text-sm text-ink-muted">
                Add a proof of address and tell us where your funds come from, and sending unlocks.
              </span>
            </span>
          </Link>
        ) : null}
      </div>

      {/* THE ACTIVITY RAIL. On desktop this sits beside the balance and actions; on mobile it falls
          straight below them. Activity always has content, so this column is never an empty gap. */}
      <div className="space-y-6">
        {/* 4. WHAT JUST HAPPENED. */}
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="font-semibold text-ink">Recent activity</h2>
            <Link to="/wallet" className="text-sm font-medium text-brand hover:underline">
              See all
            </Link>
          </div>

          {history.length === 0 ? (
            <EmptyState
              title="Nothing has happened yet"
              body="When you send or receive money, it will show up here."
            />
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {history.map((t) => {
                const incoming = t.direction === 'in';
                return (
                  <li key={t.id}>
                    <Link
                      to={`/activity/${t.correlationId}`}
                      className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                          incoming ? 'bg-accent/12 text-accent' : 'bg-primary/10 text-brand'
                        }`}
                      >
                        {incoming ? <ReceiveIcon /> : <SendIcon />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">
                          {incoming ? 'Received' : 'Sent'}
                        </p>
                        <p className="truncate text-xs text-ink-subtle">
                          {fmtDateShort(t.at)} &middot; {t.state}
                        </p>
                      </div>
                      <span
                        className={`tabular shrink-0 text-sm font-semibold ${
                          incoming ? 'text-accent' : 'text-ink'
                        }`}
                      >
                        {incoming ? '+' : '−'} {t.asset} {formatWireAmount(t.amount)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {locks.length > 0 ? (
          <section>
            <h2 className="mb-2 font-semibold text-ink">Why some of your money is held</h2>
            <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {locks.map((l, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  {/* The plain-language reason, which the BACKEND supplies. The same words the operator
                    and the regulator see, so a support call is not a translation exercise. */}
                  <span className="text-ink-muted">{l.reasonLabel}</span>
                  <span className="tabular font-medium text-ink">
                    {l.asset} {formatWireAmount(l.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function QuickAction({
  href,
  label,
  enabled,
  locked,
  icon,
}: {
  href: string;
  label: string;
  enabled: boolean;
  locked: string;
  icon: ReactNode;
}) {
  if (!enabled) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-card border border-border bg-surface p-4 text-center opacity-60">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-sunken text-ink-subtle">
          {icon}
        </span>
        <p className="mt-2 text-sm font-medium text-ink">{label}</p>
        <p className="mt-0.5 text-micro text-ink-subtle">Needs {locked}</p>
      </div>
    );
  }

  return (
    <Link
      to={href}
      className="flex h-full flex-col items-center justify-center rounded-card border border-border bg-surface p-4 text-center shadow-card transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-soft"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-brand">
        {icon}
      </span>
      <p className="mt-2 text-sm font-medium text-ink">{label}</p>
    </Link>
  );
}

function NotVerifiedYet() {
  return (
    <section className="rounded-xl border border-border bg-surface p-6 text-center shadow-card">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-brand">
        <LockIcon />
      </span>
      <h2 className="mt-4 font-semibold text-ink">Your account is not verified yet</h2>
      <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
        You can look around, but you cannot hold or move money until we have verified who you are.
        That is the rule this platform is built on, and it is why nobody transacts anonymously here.
      </p>
      <Link
        to="/verify/tier1"
        className="mt-5 inline-flex rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
      >
        Verify my identity
      </Link>
    </section>
  );
}

// Icons. 20x20 stroke set, consistent weight, matching the shell's line style.
function SendIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M6 18 18 6m0 0h-8m8 0v8"
      />
    </svg>
  );
}

function ReceiveIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M18 6 6 18m0 0h8m-8 0v-8"
      />
    </svg>
  );
}

function ConvertIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M7.5 7.5h11m0 0-3-3m3 3-3 3M16.5 16.5h-11m0 0 3 3m-3-3 3-3"
      />
    </svg>
  );
}

/** A till receipt with a coin on it: collecting money, not sending it. */
function TillIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M6 4h12a1 1 0 0 1 1 1v15l-2.5-1.5L14 20l-2-1.5L10 20l-2.5-1.5L5 20V5a1 1 0 0 1 1-1Zm2.5 4h7m-7 3.5h7m-7 3.5h4"
      />
    </svg>
  );
}

function WithdrawIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M12 3v11m0 0 4-4m-4 4-4-4M5 17v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2"
      />
    </svg>
  );
}

function TokenizeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M4 9.5 12 4l8 5.5M5.5 8.75V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.75M9.5 20v-6h5v6"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M7 11V8a5 5 0 0 1 10 0v3m-11 0h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
      />
    </svg>
  );
}
